/**
 * DeleteAppJobModal Component
 *
 * Modal for confirming appJob deletion.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { useDeleteAppJob } from '../hooks/useDeleteAppJob';
import type { AppJobSanitized } from '../../../types/app-job.types';

interface DeleteAppJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  appJob: AppJobSanitized;
}

/**
 * DeleteAppJobModal component
 */
export const DeleteAppJobModal: React.FC<DeleteAppJobModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  appJob,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { mutate: deleteAppJob, isPending } = useDeleteAppJob(tenantId, {
    onSuccess: () => {
      onClose();
    },
    onError: (error) => {
      setErrorMessage(error);
    },
  });

  const handleDelete = () => {
    setErrorMessage('');
    deleteAppJob(appJob.appJobId);
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Delete App Job</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {/* Warning */}
          <Alert variant="warning">
            This action cannot be undone. The appJob will be permanently deleted and all future
            appJobd executions will be cancelled.
          </Alert>

          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* App Job Details */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Schedule Name</span>
              <span className="text-sm font-medium text-gray-900">{appJob.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Application</span>
              <span className="text-sm font-medium text-gray-900">{appJob.applicationCode}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Function</span>
              <code className="text-sm bg-white px-2 py-0.5 rounded border">
                {appJob.functionName}
              </code>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Cron</span>
              <code className="text-sm bg-white px-2 py-0.5 rounded border">
                {appJob.cronExpression}
              </code>
            </div>
          </div>

          {/* Confirmation Text */}
          <p className="text-sm text-gray-600">
            Are you sure you want to delete the appJob <strong>"{appJob.name}"</strong>?
          </p>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="button" variant="danger" loading={isPending} onClick={handleDelete}>
              Delete App Job
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
